import { CheckCircle, XCircle } from 'lucide-react';
import { validatePasswordStrength, getPasswordStrengthLabel } from '../../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  if (!password) return null;

  const validation = validatePasswordStrength(password);
  const strength = getPasswordStrengthLabel(validation.score);
  const progressWidth = (validation.score / 5) * 100;

  return (
    <div className="mt-4 space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-300">Force du mot de passe</span>
          <span className={`text-sm font-semibold ${strength.color}`}>{strength.label}</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${strength.bgColor}`}
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-400">Critères requis :</p>
        <ul className="space-y-1.5">
          <PasswordCriteria
            met={validation.rules.minLength}
            text="Au moins 8 caractères"
          />
          <PasswordCriteria
            met={validation.rules.hasUpperCase}
            text="Une lettre majuscule (A-Z)"
          />
          <PasswordCriteria
            met={validation.rules.hasLowerCase}
            text="Une lettre minuscule (a-z)"
          />
          <PasswordCriteria
            met={validation.rules.hasNumber}
            text="Un chiffre (0-9)"
          />
          <PasswordCriteria
            met={validation.rules.hasSpecialChar}
            text="Un caractère spécial (!@#$%...)"
          />
        </ul>
      </div>
    </div>
  );
}

interface PasswordCriteriaProps {
  met: boolean;
  text: string;
}

function PasswordCriteria({ met, text }: PasswordCriteriaProps) {
  return (
    <li className="flex items-center space-x-2 text-xs">
      {met ? (
        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-slate-500 flex-shrink-0" />
      )}
      <span className={met ? 'text-green-400' : 'text-slate-400'}>{text}</span>
    </li>
  );
}
